import { lotsServices, type ApiStockLot } from "../../../../../api/lots";
import type { StockKanbanContainer, StockKanbanContainerContent } from "../../../../../api/stockKanbanContainers";
import { isStockKanbanSupplyLotAccountingCode } from "../../../helpers/stockKanbanSupplyAvailableLot";
import { fetchStockKanbanSupplyLotAvailability } from "./loadStockKanbanSupplyLotAvailability";

/** Результат поиска партии контейнера по скану. */
export type StockKanbanSupplyLotScanResult =
    | { type: "notFound" }
    | { type: "stockObjMismatch"; lot: ApiStockLot }
    | { type: "ambiguous"; lots: ApiStockLot[] }
    | { type: "single"; lot: ApiStockLot; qty: number; spCode: number | null };

/**
 * Ищет партию по скану через `stock/lots.loadByFilter` (`SoNum`), проверяет доступность
 * через `stock/lots.getAvLots` и размещение через `storePlaces.loadRemaindersByFilter`. \
 * Непартионный остаток (`LotCode === 0`) отклоняем — для КАНБАН нужен партийный учёт
 * (см. {@link isStockKanbanSupplyLotAccountingCode}). \
 * При нескольких ячейках открываем форму с `spCode: null` для ручного выбора;
 * без ячеек — `spCode: 0` («на полу»), с одной ячейкой — её код.
 * @param barcode отсканированная этикетка партии (`SoNum`)
 * @param container контейнер КАНБАН
 * @param contents уже выбранные партии контейнера
 */
export async function resolveStockKanbanSupplyLotScan(
    barcode: string,
    container: StockKanbanContainer,
    contents: StockKanbanContainerContent[],
): Promise<StockKanbanSupplyLotScanResult> {
    const lots = uniqueLots(await lotsServices.load({ flt: { SoNum: barcode } }));

    if (lots.length === 0) {
        return { type: "notFound" };
    }

    if (lots.length > 1) {
        return { type: "ambiguous", lots };
    }

    const lot = lots[0];

    if (!isStockKanbanSupplyLotAccountingCode(lot.LotCode)) {
        return { type: "stockObjMismatch", lot };
    }

    const availability = await fetchStockKanbanSupplyLotAvailability(container, lot.LotCode);

    if (availability == null) {
        return { type: "stockObjMismatch", lot };
    }

    const { cells, lotQtyRest: lotQty } = availability;
    const remainingQty = Math.max(container.Qty - sumContentQty(contents), 0);
    const qty = Math.min(lotQty, remainingQty);

    if (cells == null) {
        return { type: "single", lot, qty, spCode: 0 };
    }

    if (cells.length === 1) {
        return { type: "single", lot, qty, spCode: cells[0].SpCode };
    }

    return { type: "single", lot, qty, spCode: null };
}

function sumContentQty(contents: StockKanbanContainerContent[]): number {
    return contents.reduce((sum, item) => sum + item.Qty, 0);
}

function uniqueLots(lots: ApiStockLot[]): ApiStockLot[] {
    const result: ApiStockLot[] = [];
    const seen = new Set<number>();

    lots.forEach((lot) => {
        if (!seen.has(lot.LotCode)) {
            seen.add(lot.LotCode);
            result.push(lot);
        }
    });

    return result;
}
